<template>
    <div class="pa-ui pa-page rma-page">
        <header class="pa-page-header">
            <router-link to="/fiscalizacao-mensal/questionarios" class="pa-eyebrow">
                <v-icon small>mdi-chevron-left</v-icon>Questionários
            </router-link>
            <div class="pa-page-header__row">
                <div>
                    <h1 class="pa-title">{{ data ? data.title : "Questionário" }}</h1>
                    <p v-if="data" class="pa-subtitle">
                        Recebido em {{ formatDateTime(data.sent_at) }}
                        <span v-if="data.due_date_label"> · responder até <strong>{{ data.due_date_label }}</strong></span>
                        <span v-if="data.answered_at"> · respondido em {{ formatDateTime(data.answered_at) }}<span v-if="data.answered_by_name"> por {{ data.answered_by_name }}</span></span>
                    </p>
                </div>
                <pa-badge v-if="data" :variant="data.status === 'sent' ? 'warning' : data.status === 'answered' ? 'success' : 'neutral'" :text="data.status_label" />
            </div>
        </header>

        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

        <div v-else-if="loadError" class="pa-card rma-qf-card">
            <div class="pa-empty">
                <v-icon large>mdi-alert-circle-outline</v-icon>
                <div>{{ loadError }}</div>
            </div>
        </div>

        <template v-else-if="data">
            <div v-if="data.message" class="pa-card rma-qf-card rma-qf-message">{{ data.message }}</div>

            <div v-if="data.status === 'closed' && !data.answered_at" class="pa-card rma-qf-card rma-qf-notice">
                Este questionário foi encerrado pela Administração Judicial e não aceita mais respostas.
            </div>

            <v-form ref="form" @submit.prevent="submit">
                <section v-for="(item, index) in data.items" :key="item.id" class="pa-card rma-qf-card rma-qf-question">
                    <div class="rma-qf-question__title">
                        <span class="rma-qf-question__number">{{ index + 1 }}</span>
                        <span>{{ item.text }}<span v-if="item.required && editable" class="rma-qf-required" title="Obrigatória"> *</span></span>
                    </div>
                    <div v-if="item.help" class="rma-qf-question__help">{{ item.help }}</div>

                    <div v-if="editable" class="rma-qf-body">
                        <v-text-field v-if="item.answer_type === 'short_text'" v-model="answers[item.id]" outlined dense hide-details="auto" maxlength="500"
                            :rules="rules(item)" placeholder="Sua resposta"></v-text-field>
                        <v-textarea v-else-if="item.answer_type === 'long_text'" v-model="answers[item.id]" outlined rows="3" auto-grow hide-details="auto"
                            maxlength="5000" :rules="rules(item)" placeholder="Sua resposta"></v-textarea>
                        <v-radio-group v-else-if="item.answer_type === 'yes_no'" v-model="answers[item.id]" row hide-details="auto" class="mt-0" :rules="rules(item)">
                            <v-radio label="Sim" value="sim"></v-radio>
                            <v-radio label="Não" value="nao"></v-radio>
                        </v-radio-group>
                        <v-radio-group v-else-if="item.answer_type === 'single_choice'" v-model="answers[item.id]" hide-details="auto" class="mt-0" :rules="rules(item)">
                            <v-radio v-for="option in item.options" :key="option" :label="option" :value="option"></v-radio>
                        </v-radio-group>
                        <div v-else-if="item.answer_type === 'multiple_choice'">
                            <v-checkbox v-for="option in item.options" :key="option" v-model="answers[item.id]" :label="option" :value="option" dense hide-details class="mt-0"></v-checkbox>
                            <div v-if="showErrors && item.required && !(answers[item.id] || []).length" class="rma-qf-error">Escolha ao menos uma opção.</div>
                        </div>
                        <v-text-field v-else-if="item.answer_type === 'number'" v-model="answers[item.id]" outlined dense hide-details="auto" inputmode="decimal"
                            :rules="rules(item, true)" placeholder="0" style="max-width: 260px;"></v-text-field>
                        <v-text-field v-else-if="item.answer_type === 'currency'" v-model="answers[item.id]" outlined dense hide-details="auto" inputmode="decimal" prefix="R$"
                            :rules="rules(item, true)" placeholder="0,00" style="max-width: 260px;"></v-text-field>
                        <v-text-field v-else-if="item.answer_type === 'date'" v-model="answers[item.id]" type="date" outlined dense hide-details="auto"
                            :rules="rules(item)" style="max-width: 220px;"></v-text-field>
                    </div>
                    <div v-else class="rma-qf-answer" :class="{ 'rma-qf-answer--empty': formatAnswer(item) === null }">
                        {{ formatAnswer(item) === null ? "Sem resposta" : formatAnswer(item) }}
                    </div>
                </section>

                <div v-if="editable" class="rma-qf-actions">
                    <span class="rma-qf-actions__hint">Confira as respostas: depois de enviado, o questionário não pode ser alterado.</span>
                    <v-btn type="submit" color="primary" depressed large :loading="saving"><v-icon left small>mdi-send-outline</v-icon>Enviar respostas</v-btn>
                </div>
            </v-form>
        </template>
    </div>
</template>

<script>
import axios from "axios";
import PaBadge from "@/components/plan-adhesion/PaBadge.vue";
import { errorMessage, formatDate, formatDateTime, notifyError, notifySuccess } from "@/components/rma/rma.util";
import "@/components/plan-adhesion/pa-ui.scss";

// Resposta do questionario de fiscalizacao (Painel da Devedora).
export default {
    name: "RmaQuestionnaireForm",

    components: { PaBadge },

    data: () => ({ loading: true, loadError: null, data: null, answers: {}, saving: false, showErrors: false }),

    computed: {
        editable() {
            return !!this.data && this.data.status === "sent";
        },
    },

    created() {
        this.load();
    },

    methods: {
        formatDateTime,

        async load() {
            this.loading = true;
            this.loadError = null;
            try {
                const result = await axios.get(`/api/client/rma/questionnaires/${this.$route.params.id}`);
                this.data = result.data.content;
                const answers = {};
                this.data.items.forEach((item) => { answers[item.id] = item.answer_type === "multiple_choice" ? [] : ""; });
                this.answers = answers;
            } catch (error) {
                this.loadError = errorMessage(error, "Não foi possível abrir o questionário.");
            } finally {
                this.loading = false;
            }
        },

        rules(item, numeric = false) {
            const list = [];
            if (item.required) list.push((value) => (value !== null && value !== undefined && String(value).trim() !== "") || "Resposta obrigatória.");
            if (numeric) list.push((value) => !value || /^-?[\d.]*,?\d*$/.test(String(value).trim()) || /^-?\d+(\.\d+)?$/.test(String(value).trim()) || "Informe um número.");
            return list;
        },

        async submit() {
            this.showErrors = true;
            const formOk = this.$refs.form.validate();
            const multipleOk = this.data.items.every((item) => item.answer_type !== "multiple_choice" || !item.required || (this.answers[item.id] || []).length);
            if (!formOk || !multipleOk) {
                notifyError(this, null, "Responda as perguntas obrigatórias antes de enviar.");
                return;
            }
            const confirmed = await this.$dialog.confirm({
                title: "Enviar respostas",
                text: "Depois de enviado, o questionário não pode ser alterado. Confirma o envio?",
                actions: { false: "Revisar", true: { text: "Enviar", color: "primary" } },
            });
            if (!confirmed) return;
            this.saving = true;
            try {
                const result = await axios.post(`/api/client/rma/questionnaires/${this.data.id}/answer`, { answers: this.answers });
                this.data = result.data.content;
                notifySuccess(this, "Respostas enviadas à Administração Judicial.");
                window.scrollTo({ top: 0, behavior: "smooth" });
            } catch (error) {
                notifyError(this, error, "Não foi possível enviar as respostas.");
            } finally {
                this.saving = false;
            }
        },

        formatAnswer(item) {
            const value = item.answer;
            if (value === null || value === undefined || value === "" || (Array.isArray(value) && !value.length)) return null;
            switch (item.answer_type) {
                case "yes_no": return value === "sim" ? "Sim" : "Não";
                case "multiple_choice": return value.join(", ");
                case "currency": return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
                case "number": return Number(value).toLocaleString("pt-BR");
                case "date": return formatDate(value);
                default: return String(value);
            }
        },
    },
};
</script>

<style scoped lang="scss">
.rma-qf-card {
    padding: 18px 22px;
    margin-bottom: 12px;
}

.rma-qf-message {
    border-left: 3px solid #2b2050;
    white-space: pre-line;
}

.rma-qf-notice {
    border-left: 3px solid #b45309;
    color: #8a5a00;
}

.rma-qf-question__title {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-weight: 600;
    font-size: 15px;
    margin-bottom: 4px;
}

.rma-qf-question__number {
    flex: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #f1effa;
    color: #2b2050;
    font-size: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.rma-qf-question__help {
    font-size: 13px;
    color: #6b6880;
    margin: 0 0 10px 34px;
}

.rma-qf-body {
    margin-left: 34px;
}

.rma-qf-body > .v-input {
    margin-top: 8px;
}

.rma-qf-required {
    color: #b42318;
}

.rma-qf-error {
    color: #b42318;
    font-size: 12px;
    margin-top: 4px;
}

.rma-qf-answer {
    margin: 8px 0 0 34px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #f0fdf4;
    white-space: pre-line;
}

.rma-qf-answer--empty {
    background: #f4f4f7;
    color: #6b6880;
}

.rma-qf-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 16px;
}

.rma-qf-actions__hint {
    font-size: 13px;
    color: #6b6880;
}
</style>
